import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { accountingApi } from './api'
import { ACCOUNT_LABELS } from './format'

export function AccountsPage() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['accounting', 'accounts'], queryFn: accountingApi.accounts })
  const [scope, setScope] = useState('*')
  const [draft, setDraft] = useState<Record<string, string>>({})
  const save = useMutation({
    mutationFn: ({ key, name }: { key: string; name: string }) => accountingApi.putAccount(scope, key, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounting', 'accounts'] }),
  })
  if (!data) return <p>Loading…</p>
  const current = (key: string) => data.rows.find((r) => r.scope === scope && r.key === key)?.account_name ?? ''
  const inherited = (key: string) => data.rows.find((r) => r.scope === '*' && r.key === key)?.account_name ?? ''
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <h1 className="font-serif text-2xl">QuickBooks accounts</h1>
      <p className="text-paper/70">Type each account exactly as it is named in QuickBooks. "All titles" applies everywhere; a title's own entry overrides it. The journal cannot be downloaded until every account it uses is filled in.</p>
      <label className="flex flex-col">Applies to
        <select className="mt-1 w-60 rounded border border-ink-400 bg-ink-900 px-2 py-1" value={scope} onChange={(e) => { setScope(e.target.value); setDraft({}) }}>
          {data.scopes.map((s) => <option key={s} value={s}>{s === '*' ? 'All titles' : s}</option>)}
        </select>
      </label>
      <table className="w-full">
        <tbody>
          {data.keys.map((key) => (
            <tr key={key} className="border-t border-ink-600">
              <td className="py-2 pr-3">{ACCOUNT_LABELS[key] ?? key}</td>
              <td className="py-2">
                <input aria-label={ACCOUNT_LABELS[key] ?? key} className="w-full rounded border border-ink-400 bg-ink-900 px-2 py-1"
                  placeholder={scope !== '*' ? inherited(key) || 'not set' : 'not set'}
                  value={draft[key] ?? current(key)} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
                  onBlur={() => { if (draft[key] !== undefined && draft[key] !== current(key)) save.mutate({ key, name: draft[key] }) }} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {save.isError && <p role="alert" className="text-red-300">Could not save that account.</p>}
    </div>
  )
}
